import React, { useState, useEffect } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Building2, Plus, Search, MoreVertical, Download, Pencil, Trash2, X, Save, Loader2, Palette, Eye, EyeOff, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', code: '', address: '', email_domains: '' });
  const [openMenu, setOpenMenu] = useState(null);
  const [brandCompany, setBrandCompany] = useState(null);

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
    } catch (error) { console.error('Failed to fetch companies:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        email_domains: (formData.email_domains || '')
          .split(',').map(s => s.trim().toLowerCase()).filter(Boolean),
      };
      if (editingId) {
        await axios.patch(`${API_URL}/companies/${editingId}`, payload, { withCredentials: true });
        toast.success('Company updated');
      } else {
        await axios.post(`${API_URL}/companies`, payload, { withCredentials: true });
        toast.success('Company created');
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({ name: '', code: '', address: '', email_domains: '' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleEdit = (c) => {
    setEditingId(c.id);
    setFormData({ name: c.name || '', code: c.code || '', address: c.address || '', email_domains: (c.email_domains || []).join(', ') });
    setShowModal(true);
    setOpenMenu(null);
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete company "${c.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/companies/${c.id}`, { withCredentials: true });
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) { toast.error(error.response?.data?.detail || 'Delete failed'); }
    setOpenMenu(null);
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="companies-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Building2 className="inline-block w-8 h-8 mr-2 mb-1" /> Companies
            </h1>
            <p className="mt-1 text-sm text-slate-600">Manage company entities within your tenant</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" data-testid="companies-export-btn" onClick={() => {
              if (filteredCompanies.length === 0) { toast.error('No data'); return; }
              downloadCSV(filteredCompanies.map(c => ({ Name: c.name, Code: c.code, Address: c.address || '' })), 'companies.csv');
              toast.success('Exported');
            }}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button data-testid="add-company-btn" onClick={() => { setEditingId(null); setFormData({ name: '', code: '', address: '' }); setShowModal(true); }}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </div>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input data-testid="search-companies-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search companies..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="companies-table">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Company Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Address</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Created</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filteredCompanies.length === 0 ? (
                <tr><td colSpan="5" className="text-center py-12 text-slate-500">No companies found</td></tr>
              ) : filteredCompanies.map(company => (
                <tr key={company.id} data-testid={`company-row-${company.code}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-sm text-slate-900 font-medium">{company.name}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">{company.code}</span>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">{company.address || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm text-slate-600">{company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td className="py-3 px-4 text-right relative">
                    <button data-testid={`company-menu-${company.id}`} onClick={() => setOpenMenu(openMenu === company.id ? null : company.id)}
                      className="p-2 hover:bg-slate-100 rounded-md">
                      <MoreVertical className="w-4 h-4 text-slate-500" />
                    </button>
                    {openMenu === company.id && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setOpenMenu(null)} />
                        <div className="absolute right-4 top-full mt-1 z-40 bg-white border border-slate-200 rounded-lg shadow-xl w-40 py-1" data-testid={`company-dropdown-${company.id}`}>
                          <button onClick={() => handleEdit(company)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex items-center gap-2" data-testid={`edit-company-${company.id}`}>
                            <Pencil className="w-3.5 h-3.5 text-slate-500" /> Edit
                          </button>
                          <button onClick={() => { setBrandCompany(company); setOpenMenu(null); }} className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm text-purple-700 flex items-center gap-2" data-testid={`brand-company-${company.id}`}>
                            <Palette className="w-3.5 h-3.5" /> Branding
                          </button>
                          <button onClick={() => handleDelete(company)} className="w-full text-left px-3 py-2 hover:bg-red-50 text-sm text-red-600 flex items-center gap-2" data-testid={`delete-company-${company.id}`}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="company-modal">
          <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b bg-slate-50 rounded-t-xl">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Company' : 'Add Company'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Company Name *</label>
                <input data-testid="company-name-input" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. Acme Corporation" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#0052FF]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Code</label>
                <input data-testid="company-code-input" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="ACME" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0052FF]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Address</label>
                <textarea data-testid="company-address-input" value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})}
                  rows={2} placeholder="Office address" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#0052FF]" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email Domains <span className="text-slate-400 font-normal">(comma-separated, for login auto-detect &amp; per-company branding)</span></label>
                <input data-testid="company-domains-input" value={formData.email_domains} onChange={e => setFormData({...formData, email_domains: e.target.value})}
                  placeholder="hecs.com, hecs.co.in" className="w-full border-2 border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#0052FF]" />
                <p className="text-[11px] text-slate-500 mt-1">When users type an email matching these domains on the login page, this company&apos;s brand (colors, logo, font) is auto-applied.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t bg-slate-50 rounded-b-xl">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button data-testid="company-save-btn" onClick={handleSubmit} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {brandCompany && (
        <CompanyBrandingModal
          company={brandCompany}
          onClose={() => setBrandCompany(null)}
          onSaved={() => { setBrandCompany(null); fetchCompanies(); }}
        />
      )}
    </MainLayout>
  );
};

// =============================================================================
// COMPANY BRANDING MODAL — per-company override (falls back to tenant brand)
// =============================================================================
const CompanyBrandingModal = ({ company, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState(null);
  const [original, setOriginal] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const dirty = !!(b && original && JSON.stringify(b) !== JSON.stringify(original));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies/${company.id}/branding`, { withCredentials: true });
        setHasOverride(!!data._has_override);
        const clean = { ...data };
        delete clean._has_override; delete clean._company_id; delete clean._company_name;
        setB(clean);
        setOriginal(clean);
      } catch { toast.error('Failed to load branding'); }
      finally { setLoading(false); }
    })();
  }, [company.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/companies/${company.id}/branding`, b, { withCredentials: true });
      toast.success(`Branding saved for ${company.name}. Switch to this company to see the new theme.`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const removeOverride = async () => {
    if (!window.confirm(`Remove ${company.name}'s brand override? It will inherit the tenant brand.`)) return;
    try {
      await axios.delete(`${API_URL}/companies/${company.id}/branding`, { withCredentials: true });
      toast.success('Override removed — tenant brand inherited');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const requestClose = () => {
    if (dirty && !window.confirm('You have unsaved branding changes that will be LOST. Close anyway?')) return;
    onClose();
  };

  if (loading || !b) {
    return <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white" /></div>;
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="company-branding-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Palette className="w-5 h-5 text-purple-600" /> Branding for {company.name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {hasOverride
                ? <span className="text-purple-700 font-semibold">● Custom override active</span>
                : <span>Inheriting tenant brand — saving here will create a company-specific override</span>}
            </p>
          </div>
          <button onClick={requestClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {dirty && (
          <div className="mx-5 mt-4 px-4 py-2 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2 text-xs text-amber-900">
            <span className="font-semibold">⚠ Unsaved changes</span>
            <span className="opacity-80">— click <strong>Save Branding</strong> below.</span>
          </div>
        )}

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {[
              ['primary_color', 'Primary'],
              ['secondary_color', 'Secondary'],
              ['accent_color', 'Accent'],
              ['email_primary_color', 'Email Primary'],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <input type="color" data-testid={`co-brand-${k}`} value={b[k] || '#000000'} onChange={e => setB({ ...b, [k]: e.target.value })} className="w-10 h-10 border border-slate-200 rounded cursor-pointer" />
                  <input value={b[k] || ''} onChange={e => setB({ ...b, [k]: e.target.value })} className="flex-1 border-2 border-slate-200 rounded-lg px-3 py-2 text-xs font-mono" />
                </div>
              </div>
            ))}
          </div>

          {[
            ['app_name', 'App Name'],
            ['login_subtitle', 'Login Subtitle'],
            ['logo_url', 'Logo URL'],
            ['favicon_url', 'Favicon URL'],
            ['font_family', 'Font Family'],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="block text-xs font-semibold text-slate-700 mb-1">{label}</label>
              <input value={b[k] || ''} onChange={e => setB({ ...b, [k]: e.target.value })} className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          ))}

          <div className="border-2 border-slate-200 rounded-xl p-5 bg-white">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-3">Live Preview</p>
            <div className="flex items-center gap-3 mb-3">
              {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-10 w-10 rounded object-contain" /> : <div className="h-10 w-10 rounded" style={{ background: b.primary_color }} />}
              <div>
                <h3 className="text-lg font-bold" style={{ color: b.secondary_color, fontFamily: b.font_family }}>{b.app_name || 'Payroll'}</h3>
                <p className="text-xs text-slate-500">{b.login_subtitle || 'Welcome back. Sign in to continue.'}</p>
              </div>
            </div>
            <button className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: b.primary_color }}>Sign in</button>
            <button className="ml-2 px-4 py-2 rounded-lg text-sm font-semibold border-2" style={{ borderColor: b.accent_color, color: b.accent_color }}>SSO Login</button>
          </div>
        </div>

        <div className="px-6 py-4 border-t bg-slate-50 flex items-center justify-between gap-2 sticky bottom-0">
          <div className="text-xs">
            {dirty ? <span className="text-amber-700 font-semibold">● Unsaved changes</span> : <span className="text-slate-400">{hasOverride ? 'Override saved' : 'No override'}</span>}
          </div>
          <div className="flex gap-2">
            {hasOverride && (
              <Button variant="outline" data-testid="co-brand-remove-btn" onClick={removeOverride} className="text-red-600 hover:bg-red-50 border-red-200">
                <Trash2 className="w-4 h-4 mr-2" /> Remove Override
              </Button>
            )}
            <Button variant="outline" onClick={requestClose}>Cancel</Button>
            <Button data-testid="co-brand-save-btn" onClick={save} disabled={saving || !dirty}
              className={`text-white ${dirty ? 'bg-purple-600 hover:bg-purple-700 ring-2 ring-purple-300 shadow-lg shadow-purple-200' : 'bg-slate-300 cursor-not-allowed'}`}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Branding
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Companies;
